<script setup lang="ts">
import { ref, watch, onBeforeUnmount, computed } from "vue";
import { useToast } from "@/core/composables/use-toast";
import CustomButton from "@/core/components/CustomButton.vue";
import IconManager from "@/core/IconManager.vue";
import ImageCropper from "@/core/components/ImageCropper.vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  accept: string;
  title?: string;
  buttonStyles?: string;
  file?: File | null;
  existingUrl?: string | null;
  saveCallBack?: () => Promise<boolean>;
  deleteCallBack?: () => Promise<boolean>;
  buttonIsLoading?: boolean;
}>();

const emit = defineEmits<{
  "update:file": [file: File | null];
}>();

const isImage = computed(() => props.accept.startsWith("image/"));
const fileWasChanged = ref<boolean>(false);

const { showError } = useToast();
const { t } = useI18n();

const selectedFile = ref<File | null>(null);
const previewUrl = ref<string>("");
const isDragging = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const showCropper = ref(false);
const pendingImageSrc = ref("");

const MAX_WIDTH = 480;
const MAX_HEIGHT = 480;

//choose if it always gives the possibility to crop or if only on images exceding the maxlimit
const ALWAYS_CROP_IMAGES = false;

const displayUrl = computed(() => previewUrl.value || props.existingUrl || "");

function onFileSelected(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) setFile(file);
}

function onDrop(event: DragEvent) {
  isDragging.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file) setFile(file);
}

function isFileTypeAllowed(file: File): boolean {
  const rules = props.accept.split(",").map((r) => r.trim());
  return rules.some((rule) => {
    if (rule.startsWith(".")) {
      return file.name.toLowerCase().endsWith(rule.toLowerCase());
    }
    if (rule.endsWith("/*")) {
      return file.type.startsWith(rule.slice(0, -1));
    }
    return file.type === rule;
  });
}

async function setFile(file: File) {
  if (!isFileTypeAllowed(file)) {
    showError({ message: t("message.invalidType") });
    return;
  }
  if (isImage.value) {
    let needsCrop = ALWAYS_CROP_IMAGES;

    if (!needsCrop) {
      try {
        const dimensions = await getImageDimensions(file);
        needsCrop =
          dimensions.width > MAX_WIDTH || dimensions.height > MAX_HEIGHT;
      } catch {
        //A format the browser cannot decode (heic, for one) never reaches the api anyway
        showError({ message: t("message.invalidType") });
        return;
      }
    }

    if (needsCrop) {
      pendingImageSrc.value = URL.createObjectURL(file);
      showCropper.value = true;
      return;
    }
  }

  applyFile(file);
}

function openFilePicker() {
  fileInput.value?.click();
}

async function clearFile() {
  if (!(await props.deleteCallBack?.())) return;
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
  }
  selectedFile.value = null;
  previewUrl.value = "";
  fileWasChanged.value = false;
  if (fileInput.value) fileInput.value.value = "";
  emit("update:file", null);
}

watch(
  () => props.file,
  (newFile) => {
    if (newFile === selectedFile.value) return;
    if (previewUrl.value) {
      URL.revokeObjectURL(previewUrl.value);
    }

    if (newFile) {
      selectedFile.value = newFile;
      previewUrl.value = URL.createObjectURL(newFile);
    } else {
      selectedFile.value = null;
      previewUrl.value = "";
      if (fileInput.value) fileInput.value.value = "";
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
  }
  if (pendingImageSrc.value) {
    URL.revokeObjectURL(pendingImageSrc.value);
  }
});

//Cropper
function applyFile(file: File) {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  selectedFile.value = file;
  previewUrl.value = URL.createObjectURL(file);
  fileWasChanged.value = true;
  emit("update:file", file);
}

function onCropConfirm(croppedFile: File) {
  showCropper.value = false;
  URL.revokeObjectURL(pendingImageSrc.value);
  pendingImageSrc.value = "";
  applyFile(croppedFile);
}

function onCropCancel() {
  showCropper.value = false;
  URL.revokeObjectURL(pendingImageSrc.value);
  pendingImageSrc.value = "";
  if (fileInput.value) fileInput.value.value = "";
}

function getImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
   return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    img.src = url;
  });
}
</script>

<template>
  <div class="flex flex-col gap-4 bg-white rounded-xl shadow-md p-4 w-full">
    <div class="flex flex-row w-full justify-between">
      <span class="font-extrabold">{{ $props.title }}</span>
      <span class="text-light-gray"></span>
    </div>
    <div
      class="drop-zone flex bg-background border border-dashed rounded-xl justify-center items-center h-full"
      :class="{ 'drop-zone--active': isDragging }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <slot
        name="preview"
        :file="selectedFile"
        :preview-url="displayUrl"
        :is-image="isImage"
      >
        <div v-if="displayUrl" class="preview">
          <img :src="displayUrl" alt="Selected image preview" />
        </div>
        <div
          v-if="!displayUrl"
          class="flex flex-col justify-center items-center"
        >
          <IconManager name="Upload" class="size-10 text-primary" />
          <p>{{ t("fileUploader.doUpload") }}</p>
        </div>
      </slot>
    </div>
    <div class="flex flex-row gap-4">
      <CustomButton
        v-if="displayUrl"
        :title="t('common.delete')"
        @click="clearFile"
        :is-loading="buttonIsLoading"
      >
        <IconManager name="Delete"
      /></CustomButton>

      <CustomButton
        :title="
          displayUrl.length == 0
            ? t('fileUploader.upload')
            : t('fileUploader.replace')
        "
        @click="openFilePicker"
      >
        <IconManager :name="displayUrl.length == 0 ? 'Box' : 'ReplaceImage'"
      /></CustomButton>

      <CustomButton
        :title="t('fileUploader.save')"
        @click="
          async () => (fileWasChanged = !((await saveCallBack?.()) ?? true))
        "
        :disabled="!fileWasChanged"
        :is-loading="buttonIsLoading"
      >
        <IconManager name="Save"
      /></CustomButton>
    </div>
    <input
      type="file"
      ref="fileInput"
      :accept="accept"
      @change="onFileSelected"
      style="display: none"
    />
  </div>
  <ImageCropper
    v-if="showCropper"
    :image-src="pendingImageSrc"
    :max-width="MAX_WIDTH"
    :max-height="MAX_HEIGHT"
    @confirm="onCropConfirm"
    @cancel="onCropCancel"
  />
</template>